/**
 * Shop / Marketplace / RCD Market API contracts.
 * Amounts are decimal integer strings in base units (RCD has 0 decimals; SOL in lamports).
 */
import { z } from "zod";
import { Address, U64String } from "./ids.js";

export const Amount = U64String;
export type Amount = z.infer<typeof Amount>;

export const CommerceIntentStatus = z.enum([
  "created",
  "built",
  "submitted",
  "confirmed",
  "finalized",
  "failed",
  "expired",
]);
export type CommerceIntentStatus = z.infer<typeof CommerceIntentStatus>;

export const CommerceTxStatus = z.enum([
  "built",
  "submitted",
  "confirmed",
  "finalized",
  "failed",
  "expired",
]);
export type CommerceTxStatus = z.infer<typeof CommerceTxStatus>;

export const CommerceReason = z.enum([
  "quote_expired",
  "listing_unavailable",
  "active_session",
  "sync_pending",
  "asset_reserved",
  "not_eligible",
  "insufficient_balance",
  "insufficient_sol",
  "pool_unavailable",
  "product_unavailable",
  "idempotency_conflict",
  "not_registered",
  "slippage_exceeded",
  "already_listed",
  "not_owner",
]);
export type CommerceReason = z.infer<typeof CommerceReason>;

/** Marketplace fee: 250 bps of sale RCD to treasury. */
export const MARKETPLACE_FEE_BPS = 250;

export const RcdSwapSide = z.enum(["buy", "sell"]);
export type RcdSwapSide = z.infer<typeof RcdSwapSide>;

export const RcdMarketQuoteRequest = z.object({
  side: RcdSwapSide,
  /** Exact-in: SOL lamports when buying RCD, RCD base units when selling. */
  amountIn: Amount,
  /** Slippage as basis points (e.g. 50 = 0.5%). Default applied server-side if omitted. */
  slippageBps: z.number().int().min(0).max(5_000).optional(),
});
export type RcdMarketQuoteRequest = z.infer<typeof RcdMarketQuoteRequest>;

export const RcdMarketQuoteResponse = z.object({
  quoteId: z.string().uuid(),
  side: RcdSwapSide,
  amountIn: Amount,
  expectedOut: Amount,
  minimumOutputAmount: Amount,
  poolFee: Amount,
  networkFeeEstimate: Amount,
  /** Lamports reserved for BUY (network + priority + WSOL/ATA rent). */
  maxSolReserve: Amount.optional(),
  slippageBps: z.number().int(),
  expiresAt: z.string(),
  contextSlot: z.number().int(),
  rateLabel: z.string(),
});
export type RcdMarketQuoteResponse = z.infer<typeof RcdMarketQuoteResponse>;

export const RcdMarketBuildRequest = z.object({
  quoteId: z.string().uuid(),
  idempotencyKey: z.string().min(8).max(128),
});
export type RcdMarketBuildRequest = z.infer<typeof RcdMarketBuildRequest>;

export const RcdMarketBuildResponse = z.object({
  swapId: z.string().uuid(),
  transaction: z.string(),
  minimumOutputAmount: Amount,
  expiresAt: z.string(),
});
export type RcdMarketBuildResponse = z.infer<typeof RcdMarketBuildResponse>;

export const RcdMarketSubmitRequest = z.object({
  swapId: z.string().uuid(),
  signedTransaction: z.string(),
});
export type RcdMarketSubmitRequest = z.infer<typeof RcdMarketSubmitRequest>;

export const RcdMarketStatusResponse = z.object({
  swapId: z.string().uuid(),
  status: CommerceTxStatus,
  signature: z.string().nullable(),
  amountIn: Amount,
  amountOut: Amount.nullable(),
  reason: z.string().nullable(),
});
export type RcdMarketStatusResponse = z.infer<typeof RcdMarketStatusResponse>;

export const MarketListingStatus = z.enum([
  "open",
  "sold",
  "cancelled",
  "expired",
]);
export type MarketListingStatus = z.infer<typeof MarketListingStatus>;

export const MarketListing = z.object({
  listingId: z.string().uuid(),
  asset: Address,
  seller: Address,
  priceRcd: Amount,
  status: MarketListingStatus,
  species: z.number().int().nullable().optional(),
  nickname: z.string().nullable().optional(),
  level: z.number().int().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  details: z.array(z.string()).optional(),
  createdAt: z.string(),
  listingPda: Address.optional(),
  version: z.number().int().optional(),
});
export type MarketListing = z.infer<typeof MarketListing>;

export const MarketListingsResponse = z.object({
  listings: z.array(MarketListing),
  cursor: z.string().nullable(),
});
export type MarketListingsResponse = z.infer<typeof MarketListingsResponse>;

export const MarketEligibleAsset = z.object({
  asset: Address,
  species: z.number().int().nullable(),
  nickname: z.string().nullable(),
  level: z.number().int().nullable(),
  imageUrl: z.string().nullable(),
  details: z.array(z.string()),
  eligible: z.boolean(),
  reason: z.string().nullable(),
});
export type MarketEligibleAsset = z.infer<typeof MarketEligibleAsset>;

export const MarketEligibleAssetsResponse = z.object({
  assets: z.array(MarketEligibleAsset),
});
export type MarketEligibleAssetsResponse = z.infer<typeof MarketEligibleAssetsResponse>;

export const MarketListRequest = z.object({
  asset: Address,
  priceRcd: Amount,
  idempotencyKey: z.string().min(8).max(128),
  payFeeIn: z.enum(["RCD", "SOL"]).default("SOL"),
});
export type MarketListRequest = z.infer<typeof MarketListRequest>;

export const MarketBuyRequest = z.object({
  listingId: z.string().uuid(),
  idempotencyKey: z.string().min(8).max(128),
  payFeeIn: z.enum(["RCD", "SOL"]).default("SOL"),
});
export type MarketBuyRequest = z.infer<typeof MarketBuyRequest>;

export const MarketCancelRequest = z.object({
  listingId: z.string().uuid(),
  idempotencyKey: z.string().min(8).max(128),
  payFeeIn: z.enum(["RCD", "SOL"]).default("SOL"),
});
export type MarketCancelRequest = z.infer<typeof MarketCancelRequest>;

export const MarketIntentBuildResponse = z.object({
  intentId: z.string().uuid(),
  transaction: z.string(),
  feeRcd: z.number().int().optional(),
  listingId: z.string().uuid().optional(),
  priceRcd: Amount.optional(),
});
export type MarketIntentBuildResponse = z.infer<typeof MarketIntentBuildResponse>;

export const MarketIntentStatusResponse = z.object({
  intentId: z.string().uuid(),
  status: CommerceIntentStatus,
  signature: z.string().nullable(),
  reason: z.string().nullable(),
});
export type MarketIntentStatusResponse = z.infer<typeof MarketIntentStatusResponse>;

export const ShopProductKind = z.enum(["item", "mon"]);
export type ShopProductKind = z.infer<typeof ShopProductKind>;

export const ShopProduct = z.object({
  productId: z.string().uuid(),
  sku: z.string(),
  name: z.string(),
  kind: ShopProductKind,
  priceRcd: Amount,
  stock: z.number().int(),
  version: z.number().int(),
  imageUrl: z.string().nullable().optional(),
  details: z.array(z.string()).optional(),
  /** Rare-item index 0..9 when kind=item; null for mon templates. */
  itemIndex: z.number().int().min(0).max(9).nullable().optional(),
});
export type ShopProduct = z.infer<typeof ShopProduct>;

export const ShopCatalogResponse = z.object({
  products: z.array(ShopProduct),
});
export type ShopCatalogResponse = z.infer<typeof ShopCatalogResponse>;

export const ShopPurchaseRequest = z.object({
  productId: z.string().uuid(),
  productVersion: z.number().int(),
  quantity: z.number().int().min(1).max(99).default(1),
  idempotencyKey: z.string().min(8).max(128),
  payFeeIn: z.enum(["RCD", "SOL"]).default("SOL"),
});
export type ShopPurchaseRequest = z.infer<typeof ShopPurchaseRequest>;

export const ShopPurchaseBuildResponse = z.object({
  intentId: z.string().uuid(),
  transaction: z.string(),
  feeRcd: z.number().int().optional(),
  totalPriceRcd: Amount,
});
export type ShopPurchaseBuildResponse = z.infer<typeof ShopPurchaseBuildResponse>;

export const ShopPurchaseStatusResponse = z.object({
  intentId: z.string().uuid(),
  status: CommerceIntentStatus,
  signature: z.string().nullable(),
  reason: z.string().nullable(),
});
export type ShopPurchaseStatusResponse = z.infer<typeof ShopPurchaseStatusResponse>;
